import { useState, useRef, useCallback, useEffect } from 'react';
import { useSongs } from '@/context/SongContext';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import { Check, Loader2, Volume2, VolumeX } from 'lucide-react';
import { NOTES } from '@/lib/utils';
import { toast } from 'sonner';

export default function ToneGenerator({ songId, onComplete }) {
    const { addFragment } = useSongs();
    const [selectedNote, setSelectedNote] = useState(null);
    const [octave, setOctave] = useState(4);
    const [isPlaying, setIsPlaying] = useState(false);
    const [saving, setSaving] = useState(false);
    
    const audioContextRef = useRef(null);
    const oscillatorRef = useRef(null);
    const gainNodeRef = useRef(null);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopTone();
            if (audioContextRef.current) {
                audioContextRef.current.close();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getFrequency = useCallback((note, oct) => {
        // A4 = 440Hz, adjust for octave
        const baseFreq = NOTES.find(n => n.name === note)?.frequency || 440;
        const octaveDiff = oct - 4;
        return baseFreq * Math.pow(2, octaveDiff);
    }, []);

    const stopTone = useCallback(() => {
        if (oscillatorRef.current && gainNodeRef.current && audioContextRef.current) {
            const ctx = audioContextRef.current;
            gainNodeRef.current.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.1);
            setTimeout(() => {
                try {
                    oscillatorRef.current?.stop();
                } catch (e) {
                    // Already stopped
                }
            }, 150);
        }
        setIsPlaying(false);
    }, []);

    const playTone = useCallback((note) => {
        // Stop any existing tone
        stopTone();

        // Create audio context if needed
        if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }

        const ctx = audioContextRef.current;
        const frequency = getFrequency(note, octave);

        // Create oscillator
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);

        // Smooth volume envelope
        gainNode.gain.setValueAtTime(0, ctx.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.05);

        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);

        oscillator.start();
        oscillatorRef.current = oscillator;
        gainNodeRef.current = gainNode;
        setSelectedNote(note);
        setIsPlaying(true);
    }, [octave, getFrequency, stopTone]);

    const handleNoteClick = (note) => {
        if (selectedNote === note && isPlaying) {
            stopTone();
        } else {
            playTone(note);
        }
    };

    const handleSave = async () => {
        if (!selectedNote) {
            toast.error('Please select a note first');
            return;
        }

        stopTone();
        setSaving(true);
        
        try {
            const frequency = getFrequency(selectedNote, octave);
            const fragment = await addFragment(songId, {
                type: 'tone',
                label: 'Reference Pitch',
                note: `${selectedNote.split('/')[0]}${octave}`,
                frequency: Math.round(frequency * 100) / 100
            });

            toast.success('Reference pitch saved');
            onComplete(fragment?.id);
        } catch (error) {
            console.error('Error saving tone:', error);
            toast.error('Failed to save reference pitch');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6 max-w-md mx-auto">
            {/* Note grid */}
            <TooltipProvider delayDuration={200}>
                <div className="grid grid-cols-4 gap-2">
                    {NOTES.map((note) => (
                        <Tooltip key={note.name}>
                            <TooltipTrigger asChild>
                                <button
                                    onClick={() => handleNoteClick(note.name)}
                                    className={`p-3 rounded-lg border text-center transition-all duration-fast hover:scale-105 active:scale-95 ${
                                        selectedNote === note.name && isPlaying
                                            ? 'bg-primary text-primary-foreground border-primary shadow-glow'
                                            : selectedNote === note.name
                                                ? 'bg-primary/10 border-primary/50'
                                                : 'bg-card border-border hover:border-primary/50 hover:bg-muted/50'
                                    }`}
                                >
                                    <span className="text-lg font-display">{note.name.split('/')[0]}</span>
                                    {note.name.includes('/') && (
                                        <span className="text-xs text-muted-foreground block">
                                            {note.name.split('/')[1]}
                                        </span>
                                    )}
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="tooltip-animate">
                                <p className="text-xs">{Math.round(getFrequency(note.name, octave))} Hz</p>
                            </TooltipContent>
                        </Tooltip>
                    ))}
                </div>
            </TooltipProvider>

            {/* Octave slider */}
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <Label>Octave</Label>
                    <span className="text-sm font-medium text-foreground bg-muted px-2 py-0.5 rounded">{octave}</span>
                </div>
                <Slider
                    value={[octave]}
                    onValueChange={([value]) => {
                        setOctave(value);
                        if (selectedNote && isPlaying) {
                            playTone(selectedNote);
                        }
                    }}
                    min={2}
                    max={6}
                    step={1}
                    className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Low</span>
                    <span>High</span>
                </div>
            </div>

            {/* Current tone display */}
            {selectedNote && (
                <div className="flex items-center justify-center gap-4 p-4 rounded-xl bg-muted/50 animate-fade-in">
                    <div className="flex items-center gap-2">
                        {isPlaying ? (
                            <Volume2 className="w-5 h-5 text-primary animate-pulse" />
                        ) : (
                            <VolumeX className="w-5 h-5 text-muted-foreground" />
                        )}
                        <span className="text-2xl font-display text-foreground">
                            {selectedNote.split('/')[0]}{octave}
                        </span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                        {Math.round(getFrequency(selectedNote, octave))} Hz
                    </span>
                </div>
            )}

            {/* Stop/Play button */}
            {selectedNote && (
                <Button
                    variant="outline"
                    onClick={() => isPlaying ? stopTone() : playTone(selectedNote)}
                    className="w-full hover:scale-[1.02] active:scale-[0.98] transition-transform"
                >
                    {isPlaying ? (
                        <><VolumeX className="w-4 h-4 mr-2" /> Stop Tone</>
                    ) : (
                        <><Volume2 className="w-4 h-4 mr-2" /> Play Tone</>
                    )}
                </Button>
            )}

            {/* Save button */}
            <Button
                onClick={handleSave}
                disabled={saving || !selectedNote}
                className="w-full h-12 hover:scale-[1.02] active:scale-[0.98] transition-transform"
            >
                {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                    <Check className="w-4 h-4 mr-2" />
                )}
                Save Reference Pitch
            </Button>

            <p className="text-xs text-center text-muted-foreground">
                Use this to capture a starting pitch or key reference for your song.
            </p>
        </div>
    );
}